import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CrisisResourceBanner from '../../components/CrisisResourceBanner';
import Header from '../../components/Header';
import Button from '../../components/ui/Button';
import PathwaySelector from './components/PathwaySelector';
import ProgressIndicator from './components/ProgressIndicator';
import DemographicStep from './components/DemographicStep';
import CulturalPreferencesStep from './components/CulturalPreferencesStep';
import CurrentIssuesStep from './components/CurrentIssuesStep';
import SeverityAssessmentStep from './components/SeverityAssessmentStep';
import HelperPreferencesStep from './components/HelperPreferencesStep';
import ConsentPrivacyStep from './components/ConsentPrivacyStep';
import IntakeHeader from './components/IntakeHeader';
import ProgressSaveIndicator from './components/ProgressSaveIndicator';
import { useIntakeForm } from './hooks/useIntakeForm';
import { validateIntakeStep } from './utils/validation';
import { seekerIntakeService } from '../../services/seekerIntakeService';
import { supabase } from '../../lib/supabase';

const SeekerIntakeFlow = () => {
  const navigate = useNavigate();
  const [selectedPathway, setSelectedPathway] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [errors, setErrors] = useState({});
  
  const { formData, handleFormChange } = useIntakeForm();

  const quickSteps = [
    { id: 'issues', label: 'Issues', component: CurrentIssuesStep },
    { id: 'severity', label: 'Urgency', component: SeverityAssessmentStep },
    { id: 'preferences', label: 'Preferences', component: HelperPreferencesStep },
    { id: 'consent', label: 'Consent', component: ConsentPrivacyStep }
  ];

  const comprehensiveSteps = [
    { id: 'demographic', label: 'Basic Info', component: DemographicStep },
    { id: 'cultural', label: 'Cultural', component: CulturalPreferencesStep },
    { id: 'issues', label: 'Issues', component: CurrentIssuesStep },
    { id: 'severity', label: 'Urgency', component: SeverityAssessmentStep },
    { id: 'preferences', label: 'Preferences', component: HelperPreferencesStep },
    { id: 'consent', label: 'Consent', component: ConsentPrivacyStep }
  ];

  const steps = selectedPathway === 'quick' ? quickSteps : comprehensiveSteps;
  const CurrentStepComponent = steps?.[currentStep]?.component;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handlePathwaySelect = (pathway) => {
    setSelectedPathway(pathway);
    setCurrentStep(0);
  };

  const handleFormChangeWithErrorClear = (field, value) => {
    handleFormChange(field, value);
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = () => {
    const currentStepId = steps?.[currentStep]?.id;
    const newErrors = validateIntakeStep(currentStepId, formData);
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < steps?.length - 1) {
        setCurrentStep((prev) => prev + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    } else {
      setSelectedPathway(null);
    }
  };

  const handleSubmit = () => {
    console.log('Intake form submitted:', formData);
    navigate('/helper-discovery', { state: { intakeData: formData } });
  };

  const handleSkipStep = () => {
    const currentStepId = steps?.[currentStep]?.id;
    const requiredSteps = ['issues', 'severity', 'preferences', 'consent'];
    
    if (!requiredSteps?.includes(currentStepId)) {
      if (currentStep < steps?.length - 1) {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    setSaveError('');
    
    try {
      // Check if user is authenticated before attempting to save
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        // Store intake data in sessionStorage to restore after authentication
        sessionStorage.setItem('pendingIntakeData', JSON.stringify(formData));
        // Redirect to sign-up with return path
        navigate('/sign-up', { state: { returnTo: '/seeker-intake-flow', message: 'Please create an account to save your intake form' } });
        return;
      }

      const { data, error } = await seekerIntakeService?.saveIntake(formData);
      
      if (error) {
        setSaveError(error?.message || 'Failed to save intake form');
        return;
      }
      
      // Clear any pending intake data after successful save
      sessionStorage.removeItem('pendingIntakeData');
      navigate('/helper-discovery');
    } catch (err) {
      setSaveError(err?.message || 'An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CrisisResourceBanner />
      <Header />
      <main className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {!selectedPathway ? (
            <PathwaySelector onSelectPathway={handlePathwaySelect} />
          ) : (
            <>
              <IntakeHeader
                selectedPathway={selectedPathway}
                currentStep={currentStep}
                steps={steps}
                onBack={handleBack}
              />

              <ProgressIndicator
                currentStep={currentStep}
                totalSteps={steps?.length}
                steps={steps}
              />

              <div className="bg-card border border-border rounded-lg p-6 md:p-8 mb-6">
                {CurrentStepComponent && (
                  <CurrentStepComponent
                    formData={formData}
                    onChange={handleFormChangeWithErrorClear}
                    errors={errors}
                  />
                )}
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  {!['issues', 'severity', 'preferences', 'consent']?.includes(steps?.[currentStep]?.id) && (
                    <Button
                      variant="ghost"
                      onClick={handleSkipStep}
                      iconName="SkipForward"
                      iconPosition="right"
                    >
                      Skip (Optional)
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {currentStep > 0 && (
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      iconName="ChevronLeft"
                      iconPosition="left"
                    >
                      Back
                    </Button>
                  )}
                  <Button
                    variant="default"
                    onClick={handleNext}
                    iconName={currentStep === steps?.length - 1 ? 'Check' : 'ChevronRight'}
                    iconPosition="right"
                  >
                    {currentStep === steps?.length - 1 ? 'Complete & Find Helpers' : 'Continue'}
                  </Button>
                </div>
              </div>

              <ProgressSaveIndicator formData={formData} pathway={selectedPathway} />

              <div className="mt-4 flex flex-col items-center">
                <button
                  onClick={handleComplete}
                  disabled={saving}
                  className="w-full bg-primary text-white py-3 px-6 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Complete & Find Helpers'}
                </button>
                {saveError && (
                  <p className="text-error text-sm mt-2">{saveError}</p>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SeekerIntakeFlow;